import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { notify } from "../../components/ui/notify";
import { Spin, Button, Select } from "antd";
import { HappyProvider } from "@ant-design/happy-work-theme";

import {
  actionAbogadoCreate,
  actionAbogadoGetById,
  actionAbogadoUpdateById,
} from "../../redux/actions/abogados/abogados";
import { PATH } from "../../redux/utils";
import "../../components/forms/tribunal/FormTribunal.css";

const api = axios.create({
  baseURL: PATH,
  headers: { "Content-Type": "application/json" },
});

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const collapseSpaces = (s) => String(s ?? "").normalize("NFC").replace(/\s+/g, " ").trim();

export default function CrearAbogadoPage() {
  const { idAbogado } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isEdit = Boolean(idAbogado);

  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [detalle, setDetalle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [ciudadesOptions, setCiudadesOptions] = useState([]);

  const [form, setForm] = useState({ nombre: "", correo: "", ciudades_ids: [] });

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => { const c = { ...e }; delete c[name]; return c; });
  };

  useEffect(() => {
    api
      .get("ciudades", { headers: authHeader() })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setCiudadesOptions(arr.map((c) => ({ label: c.nombre, value: c.id_ciudad ?? c.id })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingEdit(true);
        const data = await dispatch(actionAbogadoGetById(Number(idAbogado)));
        if (!mounted) return;
        setDetalle(data || null);
        const ciudadesIds = Array.isArray(data?.ciudades)
          ? data.ciudades.map((c) => c.id_ciudad ?? c.id ?? c)
          : [];
        setForm({
          nombre: data?.nombre ?? "",
          correo: data?.correo ?? "",
          ciudades_ids: ciudadesIds,
        });
      } catch (e) {
        if (!mounted) return;
        notify.error({
          message: "No se pudo cargar el abogado",
          description: e?.response?.data?.detail || e?.message || "Sin detalle",
          placement: "bottomRight",
        });
        setDetalle(null);
      } finally {
        if (mounted) setLoadingEdit(false);
      }
    })();
    return () => { mounted = false; };
  }, [dispatch, isEdit, idAbogado]);

  const handleBack = () =>
    navigate(isEdit ? `/abogados/${idAbogado}` : "/abogados");

  const validate = () => {
    const e = {};
    if (!collapseSpaces(form.nombre)) e.nombre = "Campo requerido";
    if (form.correo && !/^\S+@\S+\.\S+$/.test(collapseSpaces(form.correo))) {
      e.correo = "Correo inválido";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = {
      nombre: collapseSpaces(form.nombre),
      correo: collapseSpaces(form.correo) || null,
      ciudades_ids: form.ciudades_ids ?? [],
    };

    try {
      setSubmitting(true);
      if (isEdit) {
        await dispatch(actionAbogadoUpdateById(Number(idAbogado), payload));
        notify.success({ message: "Abogado actualizado", placement: "bottomRight" });
        navigate(`/abogados/${idAbogado}`);
      } else {
        const res = await dispatch(actionAbogadoCreate(payload));
        const newId = res?.id_abogado ?? res?.id;
        notify.success({ message: "Abogado creado exitosamente", placement: "bottomRight" });
        navigate(newId ? `/abogados/${newId}` : "/abogados");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al guardar";
      notify.error({ message: "No se pudo guardar", description: msg, placement: "bottomRight" });
    } finally {
      setSubmitting(false);
    }
  };

  const ready = !isEdit || !!detalle;

  return (
    <HappyProvider>
      <div className="oa-exp-page">
        {/* ── TOPBAR ─────────────────────────────────────────────────── */}
        <section className="oa-exp-topbar">
          <div className="oa-exp-topbar-left">
            <div className="oa-exp-title">
              {isEdit ? "Editando Abogado" : "Creando Abogado"}
            </div>
            <div className="oa-exp-subtitle">Directorio de Abogados</div>
          </div>
          <div className="oa-exp-topbar-right">
            <Button className="oa-exp-btn oa-exp-btn-ghost" onClick={handleBack} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              className="oa-exp-btn oa-exp-btn-primary"
              disabled={submitting}
              loading={submitting}
              onClick={handleSubmit}
              icon={
                <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                  <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L400 60.7C388 48.7 371.7 42 354.5 42H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V96zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
                </svg>
              }
            >
              {isEdit ? "Guardar cambios" : "Crear abogado"}
            </Button>
          </div>
        </section>

        {/* ── BODY ───────────────────────────────────────────────────── */}
        <main className="oa-exp-main">
          {!ready ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : (
            <section className="oa-card">
              <div className="oa-card-head">
                <h2 className="oa-card-title">
                  <span className="oa-card-icon">
                    <i className="fa-solid fa-user-tie" />
                  </span>
                  DATOS DEL ABOGADO
                </h2>
              </div>

              <div className="oa-card-body oa-grid-3">
                <div className="oa-field">
                  <label className="oa-label">
                    <span className="oa-req">*</span> Nombre completo
                  </label>
                  <input
                    className={`oa-input ${errors.nombre ? "oa-input-error" : ""}`}
                    type="text"
                    placeholder="Ej. Juan García López"
                    maxLength={150}
                    value={form.nombre}
                    onChange={(e) => setField("nombre", e.target.value)}
                  />
                  {errors.nombre && <div className="oa-error">{errors.nombre}</div>}
                </div>

                <div className="oa-field">
                  <label className="oa-label">Correo electrónico</label>
                  <input
                    className={`oa-input ${errors.correo ? "oa-input-error" : ""}`}
                    type="email"
                    placeholder="abogado@ejemplo.com"
                    maxLength={100}
                    value={form.correo}
                    onChange={(e) => setField("correo", e.target.value)}
                  />
                  {errors.correo && <div className="oa-error">{errors.correo}</div>}
                </div>

                <div className="oa-field">
                  <label className="oa-label">Ciudades donde trabaja</label>
                  <Select
                    mode="multiple"
                    placeholder="Selecciona una o más ciudades..."
                    options={ciudadesOptions}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    style={{ width: "100%" }}
                    value={form.ciudades_ids}
                    onChange={(value) => setField("ciudades_ids", value)}
                  />
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    </HappyProvider>
  );
}
